import React, { ReactNode, useEffect } from 'react'
import CommandCenterService from '../../../service/CommandCenterService'
import useUrlParams from '../hooks/useUrlParams'
import useSocket from '../hooks/useSocket'
import useError from '../hooks/useError'
import useViewer from '../hooks/useViewer'
import useLoader from '../hooks/useLoader'
import useScenario from '../hooks/useScenario'
import useOrbit from '../hooks/useOrbit'
import { isOperateMode } from '../hooks/utils'
import { SCENARIO_DEPLOYMENT_TYPES } from '../../simulate/constants'


interface OrbitProviderProps {
    children: ReactNode;
}

const OrbitProvider: React.FC<OrbitProviderProps> = ({ children }) => {

    const { socket } = useSocket()
    const { setLoading } = useLoader()
    const { setError } = useError()
    const { mission_id, sat_id, scenario_id, dashboard } = useUrlParams()
    const { set_multiplier } = useViewer()
    const { scenario } = useScenario()
    const { setOrbitData, getLaunchDetails } = useOrbit()

    const getOrbitDetails = () => {
        // For completed scenarios, always use 'summary' propagation
        // For TRUETWIN_COMMAND_CENTER, always use propagation API with appropriate propagation type
        const propagation = scenario?.['status'] === "SimulationCompleted" ? 'summary' : 'dashboard'
        CommandCenterService.getnewOrbitData(mission_id, scenario_id, propagation).then(res => {
            if (res?.data) {
                setOrbitData(res?.data)
                set_multiplier(res?.data?.documentMetadata?.clock?.multiplier)
            }
        }).catch(err => {
            setError({
                header: `Simulation Failed`,
                message: `An unexpected error occurred during the simulation. Please refresh the dashboard or contact support if the issue persists.`
            })
        }).finally(() => {
            setLoading('orbit', false)
        })
    }

    const getOperatedOrbitDetails = () => {
        CommandCenterService.getOperatedOrbit(scenario_id).then(res => {
            if (res?.data) {
                setOrbitData(res?.data)
            }
        }).catch(err => {
            const isNetwork = err?.message === 'Network Error'
            setError({
                header: 'Orbit Data Unavailable',
                message: isNetwork
                    ? 'Network error — could not load orbit data. Please check your connection.'
                    : 'Failed to load orbit data. Please refresh or contact support.',
            })
        }).finally(() => {
            setLoading('orbit', false)
        })
    }

    const operateMode = isOperateMode(dashboard, scenario)
    // For TRUETWIN_COMMAND_CENTER only, use normal scenario API instead of operate API.
    // VALIDATE uses the operate API (/test/operate/{scenarioId}/satelliteorbit).
    const deploymentType = scenario?.scenarioDeploymentType?.toUpperCase()
    const isValidateSummary = deploymentType === SCENARIO_DEPLOYMENT_TYPES.VALIDATE && dashboard === 'summary'
    const useScenarioOrbitApi = deploymentType === SCENARIO_DEPLOYMENT_TYPES.TRUETWIN_COMMAND_CENTER
    const shouldUseOperateApi = operateMode && !useScenarioOrbitApi

    useEffect(() => {
        if (isValidateSummary) {
            setLoading('orbit', false)
            return
        }
        if (!shouldUseOperateApi && scenario_id && mission_id && scenario) {
            getOrbitDetails()
        }
    }, [isValidateSummary, shouldUseOperateApi, scenario_id, mission_id, scenario])

    useEffect(() => {
        if (isValidateSummary) {
            setLoading('orbit', false)
            return
        }
        if (shouldUseOperateApi && scenario_id && socket) {
            getOperatedOrbitDetails()
            socket.onmessage = (event) => {
                try {
                    if (event.data === 'REFRESH_ORBIT_PATH') {
                        setLoading('orbit', true)
                        getOperatedOrbitDetails()
                    }
                } catch {
                    // Ignore non-JSON WebSocket messages
                }
            };
        }
    }, [isValidateSummary, shouldUseOperateApi, scenario_id, socket])


    useEffect(() => {
        if (isValidateSummary) return
        if (sat_id && shouldUseOperateApi) {
            getLaunchDetails()
        }
    }, [isValidateSummary, scenario_id, sat_id, shouldUseOperateApi])

    return children
}

export default OrbitProvider
